import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { fetchMemberById } from "../features/members/slice-extensions/members-slice-extension";
import {
  selectCurrentMember,
  selectMembersStatus,
} from "../features/members/members-slice";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import { ROUTES } from "../routes";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import Tabs from "../components/Tabs";
import MemberForm from "../components/members/member-form/MemberForm";

/** Create/edit member page shell; the form itself lives in MemberForm. */
const CreateEditMember: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();

  const currentMember = useAppSelector(selectCurrentMember);
  const membersStatus = useAppSelector(selectMembersStatus);

  const isEditMode = Boolean(id);
  const tabs = [t("members.createTabMember"), t("members.createTabGroup")];
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchMemberById(id));
    }
  }, [isEditMode, id, dispatch]);

  const handleTabSelect = (index: number) => {
    if (index === 1) {
      navigate(ROUTES.CREATE_GROUP);
      return;
    }
    setSelectedTabIndex(index);
  };

  const isFetching = isEditMode && membersStatus === LoadingStatus.Loading;
  // Edit mode renders the form only once its member is on hand, so the form's
  // pristine state is the loaded member, never blanks.
  const member = isEditMode ? currentMember ?? null : null;
  const formReady = !isEditMode || (!isFetching && member?.id === id);

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-10 lg:px-10">
          <div className="lg:max-w-[694px]">
            <Breadcrumbs />
            <div className="space-y-4 py-6 lg:space-y-6">
              <h1>{isEditMode ? t("members.editMember") : t("members.createMember")}</h1>

              <Tabs tabs={tabs} selectedIndex={selectedTabIndex} onSelect={handleTabSelect} />

              {isFetching && (
                <div
                  className="flex items-center gap-2 text-grey-30 dark:text-grey-70"
                  role="status"
                  aria-live="polite"
                >
                  <div
                    className="animate-spin rounded-full h-5 w-5 border-2 border-primary-40 border-t-transparent"
                    aria-hidden="true"
                  ></div>
                  <span>{t("members.loadingMember")}</span>
                </div>
              )}

              {formReady && <MemberForm member={member} />}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreateEditMember;
